"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Networking = void 0;
const DOM_1 = require("./DOM");
const Loader_1 = require("./Loader");
const Storage_1 = require("./Storage");
const Modal_1 = require("./Modal");
const RequestTypes_1 = require("../definitions/utils/networking/RequestTypes");
const Notify_1 = require("./Notify");
const Sentry_1 = require("./Sentry/Sentry");
const TableControl_1 = require("../../../app/controls/General/TableControl/TableControl");
var Networking;
(function (Networking) {
    class NetworkingController {
        static async createRequest(requestParameters) {
            const xhr = new XMLHttpRequest();
            xhr.open(requestParameters.type.toString(), requestParameters.url, true);
            xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
            const requestsIdentifier = `${requestParameters.type}/${requestParameters.url}`;
            if (!requestParameters.disableUniq) {
                if (this.alreadyRunningRequestByIdentifier(requestsIdentifier)) {
                    console.warn(`${requestParameters.type}/${requestParameters.url} is not unique`);
                    return;
                }
            }
            if (!requestParameters.silent) {
                Loader_1.Loader.LoaderController.appendRequest();
            }
            this.appendRequest(requestsIdentifier);
            if (requestParameters.startCallback) {
                requestParameters.startCallback();
            }
            if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                for (const disableButton of requestParameters.disableButtons) {
                    disableButton.disabled = true;
                }
            }
            console.info(`${requestParameters.type}/${requestParameters.url} requested...`);
            if (requestParameters.type === RequestTypes_1.RequestTypes.POST || requestParameters.type === RequestTypes_1.RequestTypes.PUT) {
                if (requestParameters.formData) {
                    xhr.send(requestParameters.formData);
                }
                else {
                    const requestData = requestParameters.data ? JSON.stringify(requestParameters.data) : JSON.stringify({});
                    xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
                    xhr.send(requestData);
                }
            }
            else {
                xhr.send();
            }
            this.processAbortOnRedirect(xhr);
            xhr.ontimeout = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) {
                    Loader_1.Loader.LoaderController.removeRequest();
                }
                (0, Sentry_1.SentryReportNetworkingXHRTimeout)(xhr, requestParameters);
                if (requestParameters.errorCallback) {
                    requestParameters.errorCallback();
                }
                if (requestParameters.completedCallback) {
                    requestParameters.completedCallback();
                }
                if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                    for (const disableButton of requestParameters.disableButtons) {
                        disableButton.disabled = false;
                    }
                }
                return;
            };
            xhr.onabort = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) {
                    Loader_1.Loader.LoaderController.removeRequest();
                }
                console.warn(`${requestParameters.type}/${requestParameters.url} returns abort`);
                return;
            };
            xhr.onerror = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) {
                    Loader_1.Loader.LoaderController.removeRequest();
                }
                if (requestParameters.completedCallback) {
                    requestParameters.completedCallback();
                }
                if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                    for (const disableButton of requestParameters.disableButtons) {
                        disableButton.disabled = false;
                    }
                }
                if (xhr.status === 0 && xhr.readyState === 4) {
                    console.warn(`${requestParameters.type}/${requestParameters.url} forced to quit (onerror)`);
                    return;
                }
                (0, Sentry_1.SentryReportNetworkingXHR)(xhr, requestParameters);
                if (requestParameters.errorCallback) {
                    requestParameters.errorCallback();
                }
                return;
            };
            xhr.onloadend = () => {
                this.removeRequest(requestsIdentifier);
                if (!requestParameters.silent) {
                    Loader_1.Loader.LoaderController.removeRequest();
                }
            };
            xhr.onload = () => {
                if (xhr.status !== 200) {
                    if (requestParameters.completedCallback) {
                        requestParameters.completedCallback();
                    }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                    if (xhr.status === 0 && xhr.readyState === 4) {
                        console.warn(`${requestParameters.type}/${requestParameters.url} forced to quit (onload)`);
                        return;
                    }
                    (0, Sentry_1.SentryReportNetworkingXHR)(xhr, requestParameters);
                    if (requestParameters.errorCallback) {
                        requestParameters.errorCallback();
                    }
                    return;
                }
                if (requestParameters.dontParsePayload) {
                    if (requestParameters.successCallback) {
                        requestParameters.successCallback();
                    }
                    if (requestParameters.completedCallback) {
                        requestParameters.completedCallback();
                    }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                    return;
                }
                const xhrResponse = xhr.responseText.replace('OK', '');
                if (requestParameters.rawJsonResponse) {
                    if (requestParameters.successCallback) {
                        requestParameters.successCallback(xhrResponse);
                    }
                    if (requestParameters.completedCallback) {
                        requestParameters.completedCallback(xhrResponse);
                    }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                    return;
                }
                let parsedPayload = null;
                let processedResult = null;
                try {
                    parsedPayload = JSON.parse(xhrResponse);
                    let subPayload = (parsedPayload.response) ? JSON.parse(parsedPayload.response) : null;
                    parsedPayload = subPayload ? { ...parsedPayload, ...subPayload } : parsedPayload;
                    if (!requestParameters.dontProcessPayload) {
                        processedResult = NetworkingController.processPayloadFromServer(parsedPayload, requestParameters, xhr);
                    }
                    console.info(`${requestParameters.type}/${requestParameters.url} success.`);
                    if (requestParameters.successCallback && (processedResult === null || (!processedResult.failed && !processedResult.redirect))) {
                        requestParameters.successCallback(parsedPayload);
                    }
                }
                catch (e) {
                    (0, Sentry_1.SentryReportNetworkingParseException)(xhr, e, requestParameters);
                }
                finally {
                    if (requestParameters.completedCallback && (processedResult === null || !processedResult.redirect)) {
                        requestParameters.completedCallback(parsedPayload);
                    }
                    if (requestParameters.disableButtons && requestParameters.disableButtons.length > 0) {
                        for (const disableButton of requestParameters.disableButtons) {
                            disableButton.disabled = false;
                        }
                    }
                }
            };
        }
        static processPayloadFromServer(parsedPayload, requestParams, xhr) {
            if (parsedPayload.pageTitle) {
                NetworkingController.processPayloadTitle(parsedPayload.pageTitle);
            }
            if (parsedPayload.snippets) {
                NetworkingController.processPayloadSnippets(parsedPayload.snippets, requestParams, xhr);
            }
            if (parsedPayload.wrongInputs) {
                NetworkingController.processWrongInputs(parsedPayload.wrongInputs);
            }
            if (parsedPayload.flashMessages) {
                const modalCloseByNotify = Notify_1.Notify.Handler.isModalCloseByNotify(parsedPayload.flashMessages);
                Notify_1.Notify.Handler.processMessages(parsedPayload.flashMessages, requestParams.muteSuccess ?? false, modalCloseByNotify);
                if (Notify_1.Notify.Handler.isErrorByNotify(parsedPayload.flashMessages) && !parsedPayload.snippets) {
                    return { failed: true };
                }
                if (modalCloseByNotify) {
                    NetworkingController.processCloseModal();
                }
            }
            if (parsedPayload.redirect) {
                NetworkingController.processPayloadRedirect(parsedPayload.redirect, !!parsedPayload.flashMessages);
                return { redirect: true };
            }
            return {};
        }
        static processWrongInputs(wrongInputs) {
            wrongInputs.forEach((inputName) => {
                const element = DOM_1.DOM.Parser.getHtmlInputElementByName(inputName);
                element.classList.add("invalid");
            });
        }
        static processPayloadSnippets(snippets, requestParams, xhr) {
            const keys = Object.getOwnPropertyNames(snippets);
            keys.forEach((key) => {
                try {
                    const snippetElement = DOM_1.DOM.Parser.getHtmlElement(key);
                    snippetElement.innerHTML = snippets[key];
                    if (requestParams.snippetCallback) {
                        requestParams.snippetCallback(key, snippetElement);
                    }
                    this.processLinks(snippetElement);
                    this.processSubMenu(snippetElement);
                    if (!requestParams.dontInitTableTriggers) {
                        this.processTables(snippetElement);
                    }
                }
                catch (e) {
                    (0, Sentry_1.SentryReportNetworkingParseException)(xhr, e, requestParams);
                }
            });
        }
        static processPayloadRedirect(redirect, timeout) {
            if (!timeout) {
                window.location.replace(redirect);
            }
            else {
                setTimeout(() => { window.location.replace(redirect); }, 2500);
            }
        }
        static processPayloadTitle(pageTitle) {
            try {
                DOM_1.DOM.Parser.getHtmlElement('content-title').innerText = pageTitle.title;
            }
            catch (e) { }
        }
        static processCloseModal() {
            const shownModal = Modal_1.Modal.Helpers.getShownModal();
            if (shownModal) {
                Modal_1.Modal.Helpers.closeModalByElement(shownModal);
            }
        }
        static processLinks(snippetElement) {
            const triggers = DOM_1.DOM.Parser.getElementsByDataName('redirect-url', snippetElement, true);
            for (let i = 0; i < triggers.length; i++) {
                triggers[i].addEventListener('click', function () {
                    window.location.replace(DOM_1.DOM.Parser.getDataByNameFromElement('redirectUrl', triggers[i]));
                });
            }
        }
        static processTables(snippetElement) {
            if (snippetElement.getElementsByClassName(TableControl_1.ControlsGeneralTableControl.Config.tableControlElementClassName).length > 0) {
                TableControl_1.ControlsGeneralTableControl.Control.trigger(null, snippetElement);
            }
        }
        static processSubMenu(snippetElement) {
            const toggle = snippetElement.getElementsByClassName('design-customPanel');
            if (toggle.length !== 1) {
                return;
            }
            const toggleParent = toggle[0].parentElement;
            if (!toggleParent) {
                return;
            }
            const trigger = toggle[0].getElementsByClassName('design-customPanel-control');
            if (trigger.length !== 1) {
                return;
            }
            trigger[0].addEventListener('click', () => {
                toggleParent.classList.toggle('menu-open');
            });
        }
        static appendRequest(requestIdentifier) {
            const location = window.location.href;
            let storageData = Storage_1.Storage.StorageController.getFromStorage('NetworkingController');
            if (storageData && location === storageData.location) {
                storageData.requestsList.push(requestIdentifier);
            }
            else {
                storageData = { requestsList: [requestIdentifier], location: location };
            }
            Storage_1.Storage.StorageController.saveToStorage('NetworkingController', storageData);
        }
        static removeRequests() {
            const location = window.location.href;
            const storageData = { requestsList: [], location: location };
            Storage_1.Storage.StorageController.saveToStorage('NetworkingController', storageData);
        }
        static removeRequest(requestIdentifier) {
            const location = window.location.href;
            let storageData = Storage_1.Storage.StorageController.getFromStorage('NetworkingController');
            if (storageData && location === storageData.location) {
                const index = storageData.requestsList.indexOf(requestIdentifier);
                if (index !== -1) {
                    storageData.requestsList.splice(index, 1);
                }
            }
            else {
                storageData = { requestsList: [], location: location };
            }
            Storage_1.Storage.StorageController.saveToStorage('NetworkingController', storageData);
        }
        static alreadyRunningRequestByIdentifier(requestIdentifier) {
            const location = window.location.href;
            let storageData = Storage_1.Storage.StorageController.getFromStorage('NetworkingController');
            if (storageData && location === storageData.location) {
                return storageData.requestsList.includes(requestIdentifier);
            }
            else {
                return false;
            }
        }
        static processAbortOnRedirect(xhr) {
            const controlPanelElements = DOM_1.DOM.Parser.getHtmlElements('ControlPanelControl-inside-items-container-item', undefined, true);
            for (let i = 0; i < controlPanelElements.length; i++) {
                if (DOM_1.DOM.Parser.getDataByNameFromElementOrNull('disableAbort', controlPanelElements[i]) === 'yes') {
                    continue;
                }
                controlPanelElements[i].addEventListener('click', () => { xhr.abort(); });
            }
            window.addEventListener('beforeunload', () => {
                xhr.abort();
            });
        }
    }
    Networking.NetworkingController = NetworkingController;
})(Networking || (exports.Networking = Networking = {}));
